import { v4 as uuidv4 } from 'uuid';
import { sendMetricIncrement } from '@/app/components/hotel-detail/metrics';
import { APP_NAME } from '@/utils/appUtils';
import { TSX_REQUEST_HEADER_NAME } from '@/app/pageTypes.ts';
import { HEADER_NAME_MPT } from '@lmn/nextjs-appfw/dist/common';
import type { CommonData } from '@/app/common/createCommonData.ts';
import type { TabCredentials } from '@/app/common/casperToken/tabCasperToken.ts';
import {
  getPreflightWait,
  getTabCredentials,
  reportRenewSignalFromResponse,
} from '@/app/common/casperToken/tabCasperToken.ts';
import { clientAppLog } from '../../search/logging/clientAppLog';

export class FetchStatusError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'FetchStatusError';
    this.status = status;
  }
}

// Bails out of SWR's automatic retry for a client/malformed-request error (4xx) that will
// never succeed on retry, while still retrying transient server/network failures (5xx or
// errors with no known status, e.g. a network drop).
export function shouldRetryOnServerError(error: unknown): boolean {
  return !(error instanceof FetchStatusError) || error.status >= 500;
}

/*
 * The tracing id is useful to debug backend services, this id is propagated through different systems making easier
 * to debug and tracing errors.
 *
 * This headers cannot be used in local using production services because CORS policy. The request header are not
 * allowed by Access-Control-Allow-Headers in preflight response.
 */
export default function fetchWithTracing(
  input: RequestInfo,
  traceParent: string,
  commonData: CommonData,
  init?: RequestInit | undefined
): Promise<Response> {
  const traceParentHeader = traceParent ? { [TSX_REQUEST_HEADER_NAME.TRACE_PARENT]: traceParent } : undefined;

  const traceId = uuidv4();
  const tracingHeaders = {
    'x-bf-tracing-traceid': traceId,
    'x-bf-tracing-appname': APP_NAME,
    'x-bf-tracing-spanid': '0000000',
    'x-bf-tracing-hostid': 'browser',
  };

  // MPH-1410: the tab's current pair, which the tab-token module renews in place; commonData's while it is not started.
  const send = ({ mpt, csrfToken }: TabCredentials): Promise<Response> => {
    const mptHeader = mpt ? { [HEADER_NAME_MPT]: mpt } : undefined;
    const csrfTokenHeader = csrfToken ? { [TSX_REQUEST_HEADER_NAME.CSRF_TOKEN]: csrfToken } : undefined;

    return fetch(input, {
      ...init,
      headers: {
        ...tracingHeaders,
        ...traceParentHeader,
        ...mptHeader,
        ...csrfTokenHeader,
        ...init?.headers,
      },
    }).then((response) => {
      // Error statuses included: a refused token is exactly when the server asks the tab to renew.
      reportRenewSignalFromResponse(response, mpt);
      return response;
    });
  };

  // fetch is called synchronously unless the token is about to expire or a renewal is in flight, so request order is
  // otherwise unchanged.
  const preflightWait = getPreflightWait(commonData);
  return preflightWait ? preflightWait.then(send) : send(getTabCredentials(commonData));
}

export function fetchWithTracingAndLogging<T>(
  input: RequestInfo,
  init: RequestInit | undefined,
  loggingInfo: LoggingInfo,
  traceParent: string,
  commonData: CommonData
): Promise<T> {
  return handleLogging(fetchWithTracing(input, traceParent, commonData, init), loggingInfo, input, init);
}

/*
 * Retries ONCE, only when no HTTP response arrived (network drop, e.g.
 * "TypeError: Load failed"); any 4xx/5xx rethrows immediately. Statuses are
 * never retried because the routes' server side already retries downstream
 * (createWrappedRequestWithRetry) — a client status-retry stacks a multiplier
 * onto an already-degraded backend, the INT-8739 incident shape. The retry is
 * inside the fetcher (not SWR's shouldRetryOnError) because SWR publishes the
 * error BEFORE retrying, which fires one-shot error tracking and flashes the
 * error UI even when the retry then succeeds.
 */
export async function fetchWithTracingLoggingAndRetryOnce<T>(
  input: RequestInfo,
  init: RequestInit | undefined,
  loggingInfo: LoggingInfo,
  traceParent: string,
  commonData: CommonData,
  retryDelayMs = 500
): Promise<T> {
  try {
    return await fetchWithTracingAndLogging<T>(input, init, loggingInfo, traceParent, commonData);
  } catch (error) {
    if (error instanceof FetchStatusError) {
      throw error;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, retryDelayMs);
    });
    return fetchWithTracingAndLogging<T>(input, init, loggingInfo, traceParent, commonData);
  }
}

function handleLogging<T>(
  promise: Promise<Response>,
  loggingInfo: LoggingInfo,
  input: RequestInfo,
  init: RequestInit | undefined
): Promise<T> {
  const extendedLoggingInfo = { ...loggingInfo };
  return promise
    .then((response) => {
      extendedLoggingInfo.tags.status = response.status;

      if (!response.ok) {
        sendMetricIncrement(
          `app.service-error.${loggingInfo.tags.page}.${loggingInfo.tags.service}.${response.status}`
        );
        clientAppLog({
          level: response.status >= 400 && response.status < 500 ? 'WARN' : 'ERROR',
          codeSourceName: `fetchWithTracing.${loggingInfo.tags.service}.${loggingInfo.tags.module}.${loggingInfo.tags.page}`,
          logMessage: `Fetch request error: ${extendedLoggingInfo.tags.service} - ${response.status} - ${response.statusText} - ${JSON.stringify(input)}${init?.body ? `- ${JSON.stringify(init?.body)}` : ''}`,
          url: window?.location?.href,
        });

        throw new FetchStatusError('An error occurred while fetching the data.', response.status);
      }
      const contentType = response.headers.get('content-type');
      if (loggingInfo.tags.debug) {
        clientAppLog({
          level: 'INFO',
          codeSourceName: `fetchWithTracingDebug.${loggingInfo.tags.service}.${loggingInfo.tags.module}.${loggingInfo.tags.page}`,
          logMessage: `Fetch request. REQUEST ${JSON.stringify(init)}. RESPONSE ${JSON.stringify(response)}`,
          url: window?.location?.href,
        });
      }

      if (contentType && contentType.indexOf('application/json') !== -1) {
        return response.json();
      }
      return response;
    })
    .then((result) => {
      if (result.error || result.errorType) {
        const errorType = result.error?.errorType || result.errorType || 'ERROR_TYPE_UNKNOWN';
        sendMetricIncrement(`app.service-error.${loggingInfo.tags.page}.${loggingInfo.tags.service}.${errorType}`);
        clientAppLog({
          level: 'WARN',
          codeSourceName: `fetchWithTracing.${loggingInfo.tags.service}.${loggingInfo.tags.module}.${loggingInfo.tags.page}`,
          logMessage: `Fetch response error: ${extendedLoggingInfo.tags.service} - ${JSON.stringify(result)} - ${JSON.stringify(input)}${init?.body ? `- ${JSON.stringify(init?.body)}` : ''}`,
          url: window?.location?.href,
        });
      }
      return result;
    });
}
